import {
  DateString,
  Observer,
  ObserverChange,
  ObserverChangeField,
  fullDayMonthDateString,
  timeStringFromDate,
  toDateString,
} from '@nextforce/api'
import { Text, makeStyles } from '@rneui/themed'
import { t } from 'app/i18l'
import { isEmpty } from 'lodash'
import React, { useCallback, useMemo } from 'react'
import { SectionList, View } from 'react-native'
import { SafeAreaView } from 'react-native-safe-area-context'
import { UserAvatar } from 'shared/components/user/user_avatar'

interface Props {
  userChanges: ObserverChange[]
}

interface Section {
  date: DateString
  data: ObserverChange[]
  index: number
}

export function MemberChangesView({ userChanges }: Props) {
  const styles = useStyles()
  const sections: Section[] = useMemo(
    () =>
      Object.entries(
        userChanges.reduce((acc, userChange) => {
          const date = toDateString(userChange.changedAt)
          acc[date] = acc[date] || []
          acc[date].push(userChange)
          return acc
        }, {} as { [key: string]: ObserverChange[] })
      ).map(([date, data], index) => ({
        date,
        data,
        index,
      })),
    [userChanges]
  )

  const renderItem = useCallback(
    ({ item }: { item: ObserverChange }) => {
      return (
        <View style={styles.userChangeItem}>
          <UserAvatar
            user={item.changedBy}
            size={'small'}
            showAvatarIfNoPhone
          />
          <View style={styles.changeDescriptionContainer}>
            <ChangeDescriptionView item={item} />
          </View>
          <Text style={styles.time}>{timeStringFromDate(item.changedAt)}</Text>
        </View>
      )
    },
    [styles.changeDescriptionContainer, styles.userChangeItem, styles.time]
  )

  if (isEmpty(userChanges)) {
    return (
      <View style={styles.emptyContainer}>
        <Text>{t('noRecentChanges')}</Text>
      </View>
    )
  }

  return (
    <SafeAreaView edges={['left', 'right', 'bottom']} style={styles.root}>
      <View style={styles.container}>
        <SectionList
          sections={sections}
          renderSectionHeader={({ section: { date } }) => (
            <View style={styles.dateHeaderContainer}>
              <Text style={styles.dateHeader}>
                {fullDayMonthDateString(date)}
              </Text>
            </View>
          )}
          renderItem={renderItem}
        />
      </View>
    </SafeAreaView>
  )
}

function ChangeDescriptionView({ item }: { item: ObserverChange }) {
  const styles = useStyles()
  switch (item.type) {
    case 'add':
      return (
        <Text>{`${item.changedBy.firstName} ${item.changedBy.lastName} ${t(
          'added'
        )} ${item.user.firstName} ${item.user.lastName} ${t(
          'toTheManagementTeam'
        )}.`}</Text>
      )

    case 'remove':
      return (
        <Text>
          {`${item.changedBy.firstName} ${item.changedBy.lastName}`}
          <Text style={{ color: 'red' }}> {t('removed')} </Text>
          {`${item.user.firstName} ${item.user.lastName} ${t(
            'fromTheManagementTeam'
          )}.`}
        </Text>
      )
    case 'edit':
      const EditedFields = () => {
        if (!item.changedTo) {
          return null
        }
        const EditedFieldItems = Object.keys(item.changedTo).map(field => {
          if (item.changedTo) {
            if (!isEmpty(item.changedTo[field as keyof Observer])) {
              switch (field as ObserverChangeField) {
                case 'firstName':
                  return (
                    <Text style={styles.bulletText}>{`• ${t('firstName')} ${t(
                      'changed'
                    )} ${
                      item.changedFrom?.firstName
                        ? `${t('from')} ${item.changedFrom.firstName} `
                        : ``
                    }${t('to')} ${item.changedTo.firstName}.`}</Text>
                  )
                case 'lastName':
                  return (
                    <Text style={styles.bulletText}>{`• ${t('lastName')} ${t(
                      'changed'
                    )} ${
                      item.changedFrom?.lastName
                        ? `${t('from')} ${item.changedFrom.lastName} `
                        : ``
                    }${t('to')} ${item.changedTo.lastName}.`}</Text>
                  )
                case 'phoneNumber':
                  return (
                    <Text style={styles.bulletText}>{`• ${t('phoneNumber')} ${t(
                      'changed'
                    )} ${
                      item.changedFrom?.phoneNumber
                        ? `${t('from')} ${item.changedFrom?.phoneNumber} `
                        : ''
                    }${t('to')} ${item.changedTo.phoneNumber}.`}</Text>
                  )
                case 'addedToStores':
                  return (
                    <Text style={styles.bulletText}>{`• ${t('added')} ${t(
                      'to'
                    )} ${t('stores')} ${item.changedTo.addedToStores}.`}</Text>
                  )
                case 'removedFromStores':
                  return (
                    <Text style={styles.bulletText}>{`• ${t('removed')} ${t(
                      'from'
                    )} ${t('stores')} ${
                      item.changedTo.removedFromStores
                    }.`}</Text>
                  )
                default:
                  return <></>
              }
            }
          }
          return null
        })
        return <>{EditedFieldItems}</>
      }
      return (
        <View>
          <Text>{`${item.changedBy.firstName} ${item.changedBy.lastName} ${t(
            'edited'
          ).toLowerCase()} ${item.user.firstName} ${item.user.lastName}'s ${t(
            'details'
          ).toLowerCase()}`}</Text>
          <EditedFields />
        </View>
      )
  }
}

const useStyles = makeStyles(theme => ({
  root: {
    flex: 1,
  },
  container: {
    flex: 1,
  },
  emptyContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  bulletText: {
    fontSize: 14,
    lineHeight: 16,
  },
  dateHeader: {
    fontSize: 14,
    backgroundColor: theme.colors.grey7,
    paddingVertical: 5,
    paddingHorizontal: 10,
    borderRadius: 5,
  },
  dateHeaderContainer: {
    alignItems: 'center',
    marginVertical: 10,
  },
  time: {
    alignSelf: 'flex-start',
    fontSize: 12,
    backgroundColor: theme.colors.grey7,
    borderRadius: 15,
    paddingVertical: 2,
    width: 70,
    textAlign: 'center',
    marginLeft: 5,
  },
  changeDescriptionContainer: {
    flex: 1,
    paddingLeft: 10,
  },
  userChangeItem: {
    flex: 1,
    flexDirection: 'row',
    justifyContent: 'space-between',
    padding: 10,
  },
}))
